import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const trimValue = (value) => value.trim();
const apiUrl = process.env.REACT_APP_API_URL;

// Multi-select options for Warning Flag
const WARNING_FLAG_OPTIONS = ["Jumbo Account", "AAL", "Hot Pocket"];

// Optional: tweak this if you want a different debounce duration
const POLICY_LOOKUP_DEBOUNCE_MS = 400;

const RiskFlaggingPage = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    PolicyNumber: "",
    Carrier: "",
    WarningFlag: "", // will hold a comma-separated string (e.g. "Jumbo Account, AAL")
    PlanOfAction: "",
  });

  const [selectedFlags, setSelectedFlags] = useState([]); // UI state for the multi-select
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  // Carrier lookup loading/error states
  const [carrierLoading, setCarrierLoading] = useState(false);
  const [carrierError, setCarrierError] = useState("");
  const lookupTimerRef = useRef(null);

  // Validate policy number with your original regex
  const isPolicyNumberFormatValid = useMemo(() => {
    if (!form.PolicyNumber) return false;
    return /^[A-Z0-9-]+$/i.test(form.PolicyNumber);
  }, [form.PolicyNumber]);

  const validateForm = () => {
    const newErrors = {};

    if (!form.PolicyNumber)
      newErrors.PolicyNumber = "Policy number is required";
    if (!form.Carrier) newErrors.Carrier = "Carrier is required";
    if (!form.WarningFlag) newErrors.WarningFlag = "Warning flag is required";
    if (!form.PlanOfAction)
      newErrors.PlanOfAction = "Plan of action is required";

    if (form.PolicyNumber && !/^[A-Z0-9-]+$/i.test(form.PolicyNumber)) {
      newErrors.PolicyNumber =
        "Policy number should contain only letters, numbers, and hyphens";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, multiple, selectedOptions } = e.target;

    // Handle WarningFlag multi-select separately
    if (name === "WarningFlag" && multiple) {
      const values = Array.from(selectedOptions).map((o) => o.value);
      setSelectedFlags(values);
      setForm((prev) => ({
        ...prev,
        WarningFlag: values.join(", "),
      }));
      if (errors.WarningFlag) {
        setErrors((prev) => ({ ...prev, WarningFlag: "" }));
      }
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: trimValue(value),
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleBack = () => {
    navigate("/renewals-list-risk-flag");
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    const { name } = e.currentTarget;

    // Multi-select WarningFlag shouldn't be paste-driven, ignore if targeted there
    if (name === "WarningFlag") return;

    setForm((prev) => ({
      ...prev,
      [name]: trimValue(pasted),
    }));
  };

  const handleCheckboxChange = (flag, checked) => {
  let updatedFlags = checked
    ? [...selectedFlags, flag]
    : selectedFlags.filter((f) => f !== flag);

  setSelectedFlags(updatedFlags);
  setForm((prev) => ({
    ...prev,
    WarningFlag: updatedFlags.join(", "),
  }));

  if (errors.WarningFlag && updatedFlags.length > 0) {
    setErrors((prev) => ({ ...prev, WarningFlag: "" }));
  }
};


  // --- Carrier Auto-Fetch (based on PolicyNumber) ---
  const fetchCarrierForPolicy = async (policyNumber) => {
    if (!policyNumber || !/^[A-Z0-9-]+$/i.test(policyNumber)) {
      setCarrierError("");
      setForm((prev) => ({ ...prev, Carrier: "" }));
      return;
    }

    try {
      setCarrierLoading(true);
      setCarrierError("");

      // If your API requires auth, add headers here similarly to your POST call.
      // Using the exact URL you provided:
      const url = `${apiUrl}fetch-carrier-by-policyno?PolicyNumber=${encodeURIComponent(
        policyNumber
      )}`;

      const token = localStorage.getItem("accessToken");
      const resp = await axios.get(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      // Expecting carrier to be in resp.data (adjust if your API returns a different shape)
      const carrierFromApiRaw = resp?.data?.data;
      const carrierFromApi =
        typeof carrierFromApiRaw === "string" ? carrierFromApiRaw.trim() : "";

      if (!carrierFromApi) {
        setCarrierError("No carrier found for this policy number.");
      }

      setForm((prev) => ({
        ...prev,
        Carrier: carrierFromApi || "",
      }));
    } catch (err) {
      console.error("Carrier lookup failed:", err);
      setCarrierError(
        err.response?.data?.message ||
          "Failed to fetch carrier. Please verify the policy number."
      );
      setForm((prev) => ({ ...prev, Carrier: "" }));
    } finally {
      setCarrierLoading(false);
    }
  };

  // Debounce the lookup when PolicyNumber changes
  useEffect(() => {
    if (lookupTimerRef.current) clearTimeout(lookupTimerRef.current);

    // Only attempt lookup when format looks valid
    if (isPolicyNumberFormatValid) {
      lookupTimerRef.current = setTimeout(() => {
        fetchCarrierForPolicy(form.PolicyNumber);
      }, POLICY_LOOKUP_DEBOUNCE_MS);
    } else {
      setCarrierError("");
      setForm((prev) => ({ ...prev, Carrier: "" }));
    }

    return () => {
      if (lookupTimerRef.current) clearTimeout(lookupTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.PolicyNumber, isPolicyNumberFormatValid]);

  const handleAdd = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");

      await axios.post(`${apiUrl}insert-risk-flag-record`, form, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);

      setForm({
        PolicyNumber: "",
        Carrier: "",
        WarningFlag: "",
        PlanOfAction: "",
      });
      setSelectedFlags([]);
      setCarrierError("");
    } catch (error) {
      console.error("Error adding record:", error);
      setErrors({
        general:
          error.response?.data?.message ||
          "Failed to add record. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = useMemo(() => {
    return (
      form.PolicyNumber.trim() !== "" &&
      form.Carrier.trim() !== "" &&
      form.WarningFlag.trim() !== "" &&
      form.PlanOfAction.trim() !== ""
    );
  }, [form]);

  // Color badge not as meaningful for the new flags; render simple preview chips instead
  const renderWarningFlagChips = () => {
    if (!selectedFlags.length) return null;
    return (
      <div className="mt-2">
        <small className="text-muted d-block mb-1">Selected:</small>
        <div className="d-flex flex-wrap gap-2">
          {selectedFlags.map((flag) => (
            <span
              key={flag}
              className="badge rounded-pill px-3 py-2 bg-primary"
            >
              {flag}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid px-4 py-3">
      {/* Success Message */}
      {showSuccess && (
        <div
          className="alert alert-success alert-dismissible fade show mb-4"
          role="alert"
        >
          <div className="d-flex align-items-center">
            <svg
              width="20"
              height="20"
              fill="currentColor"
              className="me-2"
              viewBox="0 0 16 16"
            >
              <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.061L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
            </svg>
            <strong>Success!</strong> Risk flag record has been added
            successfully.
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowSuccess(false)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center gap-3 mb-3">
            <button
              type="button"
              onClick={handleBack}
              className="btn btn-outline-primary d-flex align-items-center gap-2 shadow-sm"
              style={{
                borderRadius: "8px",
                padding: "8px 16px",
                transition: "all 0.3s ease",
              }}
            >
              <svg
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"
                />
              </svg>
              Back to List
            </button>
          </div>
          <div>
            <h1 className="h2 mb-1 text-dark fw-bold">Add Risk Flag</h1>
            <p className="text-muted mb-0">
              Create a new risk flag record for policy monitoring
            </p>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8 col-xl-6">
          <div
            className="card border-0 shadow-sm"
            style={{ borderRadius: "12px" }}
          >
            <div className="card-header bg-white border-bottom py-3">
              <h5 className="mb-0 fw-semibold text-dark">
                Risk Flag Information
              </h5>
            </div>
            <div className="card-body p-4">
              {/* General Error */}
              {errors.general && (
                <div
                  className="alert alert-danger d-flex align-items-center mb-4"
                  role="alert"
                >
                  <svg
                    width="20"
                    height="20"
                    fill="currentColor"
                    className="me-2"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                  </svg>
                  {errors.general}
                </div>
              )}

              {/* Carrier Lookup Error */}
              {carrierError && (
                <div className="alert alert-warning mb-4" role="alert">
                  {carrierError}
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAdd();
                }}
              >
                <div className="row g-3">
                  {/* Policy Number */}
                  <div className="col-12">
                    <label className="form-label fw-medium text-dark">
                      Policy Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="PolicyNumber"
                      value={form.PolicyNumber}
                      onChange={handleChange}
                      onPaste={handlePaste}
                      className={`form-control ${
                        errors.PolicyNumber ? "is-invalid" : ""
                      }`}
                      placeholder="Enter policy number (e.g., POL-2024-001)"
                      autoComplete="off"
                      style={{ borderRadius: "8px", padding: "12px" }}
                    />
                    {errors.PolicyNumber && (
                      <div className="invalid-feedback">
                        {errors.PolicyNumber}
                      </div>
                    )}
                    {carrierLoading && (
                      <small className="text-muted">Looking up carrier…</small>
                    )}
                  </div>

                  {/* Carrier (auto-filled & uneditable) */}
                  <div className="col-12">
                    <label className="form-label fw-medium text-dark">
                      Current Carrier <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="Carrier"
                      value={form.Carrier}
                      className={`form-control ${
                        errors.Carrier ? "is-invalid" : ""
                      }`}
                      placeholder="Auto-filled from policy number"
                      autoComplete="off"
                      style={{ borderRadius: "8px", padding: "12px" }}
                      readOnly
                    />
                    {errors.Carrier && (
                      <div className="invalid-feedback">{errors.Carrier}</div>
                    )}
                  </div>

                  {/* Warning Flag (multi-select) */}
                  <div className="col-12">
                    <label className="form-label fw-medium text-dark">
                      Warning Flag <span className="text-danger">*</span>
                    </label>

                    <div
                      className={`form-check-group ${
                        errors.WarningFlag ? "is-invalid" : ""
                      }`}
                    >
                      {WARNING_FLAG_OPTIONS.map((opt) => (
                        <div className="form-check" key={opt}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`flag-${opt}`}
                            value={opt}
                            checked={selectedFlags.includes(opt)}
                            onChange={(e) =>
                              handleCheckboxChange(opt, e.target.checked)
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`flag-${opt}`}
                          >
                            {opt}
                          </label>
                        </div>
                      ))}
                    </div>
                    {errors.WarningFlag && (
                      <div className="invalid-feedback d-block">
                        {errors.WarningFlag}
                      </div>
                    )}

                    {/* Chips preview */}
                    {renderWarningFlagChips()}
                    <small className="form-text text-muted d-block mt-1">
                      Hold Ctrl (Windows) or Cmd (Mac) to select multiple. We’ll
                      send them as a comma-separated string.
                    </small>
                  </div>

                  {/* Plan of Action */}
                  <div className="col-12">
                    <label className="form-label fw-medium text-dark">
                      Plan of Action <span className="text-danger">*</span>
                    </label>
                    <textarea
                      name="PlanOfAction"
                      value={form.PlanOfAction}
                      onChange={handleChange}
                      onPaste={handlePaste}
                      className={`form-control ${
                        errors.PlanOfAction ? "is-invalid" : ""
                      }`}
                      placeholder="Describe the action plan to address this risk..."
                      rows="4"
                      style={{ borderRadius: "8px", padding: "12px" }}
                      maxLength="500"
                    />
                    {errors.PlanOfAction && (
                      <div className="invalid-feedback">
                        {errors.PlanOfAction}
                      </div>
                    )}
                    <div className="form-text">
                      {form.PlanOfAction.length}/500 characters
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="d-grid gap-2 mt-4">
                  <button
                    type="submit"
                    disabled={loading || !isFormValid}
                    className="btn btn-primary btn-lg fw-semibold d-flex align-items-center justify-content-center gap-2"
                    style={{
                      backgroundColor:
                        loading || !isFormValid ? "#6c757d" : "#122066",
                      border: "none",
                      borderRadius: "8px",
                      padding: "12px",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {loading ? (
                      <>
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Adding Record...
                      </>
                    ) : (
                      <>
                        <svg
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                        </svg>
                        Add Risk Flag
                      </>
                    )}
                  </button>

                  {!isFormValid && (
                    <small className="text-muted text-center">
                      Please fill in all required fields to continue
                    </small>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .form-control:focus,
        .form-select:focus {
          border-color: #122066;
          box-shadow: 0 0 0 0.2rem rgba(18, 32, 102, 0.25);
        }

        .btn-primary:not(:disabled):hover {
          background-color: #0f1a57 !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        .btn-outline-primary:hover,
        .btn-outline-secondary:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .btn-outline-secondary:hover {
          background-color: #6c757d;
          border-color: #6c757d;
          color: white;
        }

        .card {
          transition: all 0.3s ease;
        }

        .alert {
          border-radius: 8px;
        }

        .form-control,
        .form-select {
          transition: all 0.3s ease;
        }

        .form-control:hover:not(:focus),
        .form-select:hover:not(:focus) {
          border-color: #122066;
        }

        .btn-sm {
          font-size: 0.75rem !important;
        }
      `}</style>
    </div>
  );
};

export default RiskFlaggingPage;
